import type { BackgroundColorToken } from '@canvas/constants/colors/tokens-types';
import type { Identity } from '@canvas/model/identity';
import { createPingRegistry } from '@canvas/ping-registry';

interface Participant {
  identity: Identity;
  colorToken: BackgroundColorToken;
}

export type DatabasePingConfig = {
  cell: {
    key: { fieldId: string; entryId: string };
    changes: {
      presence: Participant[];
      editing: { active: boolean; context: 'table' | 'panel' };
      selected: boolean;
    };
  };
  row: {
    key: { entryId: string };
    changes: {
      presence: Participant[];
      /**
       * Carries the boolean selected state — used by the checkbox effect.
       */
      selected: boolean;
    };
  };
  column: {
    key: { fieldId: string };
    changes: {
      presence: Participant[];
    };
  };
  headerCheckbox: {
    key: Record<string, never>;
    changes: {
      state: 'checked' | 'indeterminate' | 'unchecked';
    };
  };
  /**
   * Broadcast-level kind with a static singleton key. Pinged on every
   * selection change so that components needing aggregate state (counts,
   * mode, hasSelection) can subscribe without knowing individual entity keys.
   */
  database: {
    key: Record<string, never>;
    changes: {
      selectionChanged: boolean;
    };
  };
};

export const databasesPingRegistry = createPingRegistry<DatabasePingConfig>({
  encode: {
    cell: ({ fieldId, entryId }) => `cell::${fieldId}::${entryId}`,
    row: ({ entryId }) => `row::${entryId}`,
    column: ({ fieldId }) => `column::${fieldId}`,
    headerCheckbox: () => `headerCheckbox`,
    database: () => `database`,
  },
});
